import { formatDistanceStrict, formatDistanceToNow } from 'date-fns';
import { apiTime } from '@/containers/agents/library.util';
import type { TriggerHealth } from '@/types/custom-playbooks';

/**
 * A play trigger's health in words (triggers hardening H4, H5), read from
 * `trigger_state.health`: a pause the poll made after failed checks, checks
 * that keep failing, or a run live for 6 hours or more. The poll writes
 * health only while a play is on, so a failure or a live run shows only then;
 * turning a play on clears its pause, so a pause shows only while it is off.
 */

export type TriggerHealthTone =
  | 'paused'
  | 'failing'
  | 'stuck'
  | 'warning'
  | 'preparing';

export interface TriggerHealthNotice {
  tone: TriggerHealthTone;
  text: string;
}

/** The text colour of each tone. */
export const HEALTH_TONE_CLASS: Record<TriggerHealthTone, string> = {
  failing: 'text-destructive',
  warning: 'text-amber-700 dark:text-amber-300',
  paused: 'text-amber-700 dark:text-amber-300',
  stuck: 'text-amber-700 dark:text-amber-300',
  preparing: 'text-tertiary-foreground',
};

/** Null while the trigger is fine or was never checked. */
export function triggerHealthNotice(
  health: TriggerHealth | null | undefined,
  enabled: boolean,
  now: number = Date.now(),
  preparingAvailable = false
): TriggerHealthNotice | null {
  if (!health) return null;
  if (health.paused) {
    if (enabled) return null;
    return {
      tone: 'paused',
      text:
        health.paused.code === 'baseline_too_large' && preparingAvailable
          ? 'Too many records: narrow the conditions or turn on field history.'
          : 'Paused: ' + (health.paused.message || 'checks failed'),
    };
  }
  if (!enabled) return null;
  if (health.error)
    return {
      tone: 'failing',
      text: 'Failing: ' + (health.error.message || 'check failed'),
    };
  if (health.liveRun) {
    const since = apiTime(health.liveRun.since);
    if (!Number.isNaN(since))
      return {
        tone: 'stuck',
        text: 'Run live for ' + formatDistanceStrict(since, now),
      };
  }
  if (health.warning)
    return {
      tone: 'warning',
      text:
        health.warning.message ||
        'Some companies could not be checked. Narrow the job filters.',
    };
  // A HubSpot list read starting over from its first page (W1-31).
  if (health.catchingUp === true)
    return { tone: 'preparing', text: 'Catching up' };
  if (
    preparingAvailable &&
    health.baseline?.state === 'building' &&
    Number.isInteger(health.baseline.count) &&
    health.baseline.count >= 0 &&
    health.baseline.count <= 500_000
  )
    return {
      tone: 'preparing',
      text: `Preparing · ${health.baseline.count.toLocaleString('en-US')} records`,
    };
  return null;
}

/** "Checked 5 minutes ago"; blank before the first check. */
export function checkedWords(health: TriggerHealth | null | undefined): string {
  if (!health?.checkedAt) return '';
  const at = apiTime(health.checkedAt);
  if (Number.isNaN(at)) return '';
  return 'Checked ' + formatDistanceToNow(at, { addSuffix: true });
}
